import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHelmet from '@/components/base/SEOHelmet';
import { mangaCategories, getMangaByCategory } from '@/mocks/manga';
import type { MangaItem } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'rexue');
  const [mangaInCategory, setMangaInCategory] = useState<MangaItem[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setMangaInCategory(getMangaByCategory(activeCategory));
  }, [activeCategory]);

  const currentCategory = mangaCategories.find((c) => c.slug === activeCategory);

  return (
    <>
      <SEOHelmet
        title={`${currentCategory?.name || '分类'}漫画 - 番木瓜漫画免费全集 | 番木瓜漫画${currentCategory?.name || ''}免费阅读`}
        description={`番木瓜漫画${currentCategory?.name || '分类'}漫画专区，提供海量${currentCategory?.name || ''}类型免费漫画全集，支持下拉式阅读。番木瓜漫画网${currentCategory?.name || '分类'}漫画大全尽在番木瓜漫画。`}
        keywords={`番木瓜漫画,${currentCategory?.name}漫画,免费全集,番木瓜漫画免费,番木瓜漫画网,下拉式漫画,漫画大全`}
        canonicalUrl={`https://www.fanmugua.com/category/${activeCategory}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentCategory?.name || '分类'}漫画 - 番木瓜漫画`,
          description: `番木瓜漫画${currentCategory?.name || '分类'}漫画免费全集阅读专区`,
          url: `https://www.fanmugua.com/category/${activeCategory}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '番木瓜漫画',
            url: 'https://www.fanmugua.com',
          },
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen bg-background-50 pt-20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">漫画分类</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">{currentCategory?.name}</span>
          </nav>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Category Header */}
          <div className="mb-8">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900">
              {currentCategory?.name}漫画
            </h1>
            <p className="text-foreground-500 text-sm mt-2">
              {currentCategory?.description} — 番木瓜漫画免费全集阅读
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {mangaCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Manga Grid */}
          {mangaInCategory.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-14">
              {mangaInCategory.map((manga) => (
                <Link
                  key={manga.slug}
                  to={`/manga/${manga.slug}`}
                  className="group block"
                  data-product-shop
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-200">
                    <img
                      src={manga.cover}
                      alt={`${manga.title} - 番木瓜漫画免费全集`}
                      title={`${manga.title} - 番木瓜漫画${currentCategory?.name}免费漫画全集`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-2 py-1">
                      <span className="text-background-50 text-[10px]">
                        更新至第{manga.chapters}话
                      </span>
                    </div>
                    {manga.status === '已完结' && (
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-medium bg-accent-500 text-background-50">
                        {manga.status}
                      </div>
                    )}
                  </div>
                  <h3 className="font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {manga.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-foreground-500">
                    <span className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-primary-500 text-[10px]"></i>
                      {manga.rating}
                    </span>
                    <span>{(manga.views / 10000).toFixed(0)}万人气</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mb-4">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，敬请期待番木瓜漫画更新</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}